import type { Node, ReportTemplate } from "../types/template.js";

export type RegionId = "body" | "header" | "header:first" | "footer" | "footer:first";
export const REGION_IDS: RegionId[] = ["body", "header", "header:first", "footer", "footer:first"];

export function getRegion(template: ReportTemplate, region: RegionId): Node[] {
  switch (region) {
    case "body":
      return template.body;
    case "header":
      return template.header.default;
    case "header:first":
      return (template.header.firstPage ??= []);
    case "footer":
      return template.footer.default;
    case "footer:first":
      return (template.footer.firstPage ??= []);
  }
}

export function readRegion(template: ReportTemplate, region: RegionId): Node[] {
  switch (region) {
    case "body":
      return template.body;
    case "header":
      return template.header.default;
    case "header:first":
      return template.header.firstPage ?? [];
    case "footer":
      return template.footer.default;
    case "footer:first":
      return template.footer.firstPage ?? [];
  }
}

export interface NodeLocation {
  region: RegionId;
  /** Index path from the region root to the node. */
  path: number[];
}

export interface NodeEntry {
  node: Node;
  parent: Node | null;
  region: RegionId;
  path: number[];
  depth: number;
  index: number;
}

export type NodeIndex = Map<string, NodeEntry>;

/** Builds an id → entry index over the whole template. */
export function indexTemplate(template: ReportTemplate): NodeIndex {
  const index: NodeIndex = new Map();
  const visit = (nodes: Node[], parent: Node | null, region: RegionId, path: number[]) => {
    nodes.forEach((node, i) => {
      const p = [...path, i];
      index.set(node.id, { node, parent, region, path: p, depth: p.length - 1, index: i });
      if (node.children) visit(node.children, node, region, p);
    });
  };
  for (const r of REGION_IDS) visit(readRegion(template, r), null, r, []);
  return index;
}

export function findNode(template: ReportTemplate, id: string): NodeEntry | undefined {
  return indexTemplate(template).get(id);
}

/** Returns the list that contains the node at `location` and the node's index in it. */
export function listAt(template: ReportTemplate, location: NodeLocation): { list: Node[]; index: number } {
  let list = getRegion(template, location.region);
  for (let i = 0; i < location.path.length - 1; i++) {
    const n = list[location.path[i]!];
    if (!n) throw new Error("Invalid node path");
    n.children ??= [];
    list = n.children;
  }
  return { list, index: location.path[location.path.length - 1] ?? 0 };
}

export function nodeAt(template: ReportTemplate, location: NodeLocation): Node | undefined {
  const { list, index } = listAt(template, location);
  return list[index];
}

/** Ancestor chain from the region root down to (and including) the node. */
export function ancestors(index: NodeIndex, id: string): NodeEntry[] {
  const chain: NodeEntry[] = [];
  let cur = index.get(id);
  while (cur) {
    chain.unshift(cur);
    cur = cur.parent ? index.get(cur.parent.id) : undefined;
  }
  return chain;
}

export function isDescendant(index: NodeIndex, ancestorId: string, id: string): boolean {
  let cur = index.get(id);
  while (cur?.parent) {
    if (cur.parent.id === ancestorId) return true;
    cur = index.get(cur.parent.id);
  }
  return false;
}

/** Depth-first walk over every node in the template. */
export function walkTemplate(template: ReportTemplate, visit: (node: Node, region: RegionId, parent: Node | null) => void): void {
  const rec = (nodes: Node[], region: RegionId, parent: Node | null) => {
    for (const n of nodes) {
      visit(n, region, parent);
      if (n.children) rec(n.children, region, n);
    }
  };
  for (const r of REGION_IDS) rec(readRegion(template, r), r, null);
}

export function countNodes(template: ReportTemplate): number {
  let n = 0;
  walkTemplate(template, () => n++);
  return n;
}

/** Collects all block types used in the template. */
export function usedBlockTypes(template: ReportTemplate): Set<string> {
  const s = new Set<string>();
  walkTemplate(template, (n) => s.add(n.type));
  return s;
}
